import { type FormEvent, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button, ErrorNote, Field, Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { AuthFrame, messageOf } from "./AuthFrame";

export function SignUpScreen() {
  const { status, signUp } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (status === "signed_in") return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUp(email.trim(), password);
      setSent(true);
    } catch (err) {
      setError(messageOf(err, "Could not sign up."));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthFrame
        title="Check your inbox"
        subtitle={`We sent a confirmation link to ${email}. Click it and you are in.`}
      >
        <p className="text-[13px] text-muted">
          Nothing there? Check spam, or{" "}
          <Link to="/sign-in" className="text-accent">
            sign in
          </Link>{" "}
          and we will resend it.
        </p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Create your account"
      subtitle="Your website answers questions and collects leads, in an afternoon."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Work email">
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password" hint="At least 10 characters.">
          <Input
            type="password"
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        <ErrorNote message={error} />
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Creating…" : "Create account"}
        </Button>
      </form>
      <p className="mt-6 text-[13px] text-muted">
        Already have one?{" "}
        <Link to="/sign-in" className="text-accent">
          Sign in
        </Link>
      </p>
    </AuthFrame>
  );
}
